import { InstrumentedHttpDataResponse } from '../instrumentation/instrumented-http.service';
import { GraphError, GraphInnerError } from '../models/graph.model';
import { XhrServiceError } from '../services/xhr.service';

export class GraphErrorResolver {

    /**
     * Repeatedly executes a callback error handler with error codes and http status from an MS Graph response
     *
     * @param error An error returned from a graph request. Should either be an XhrServiceError or InstrumentedHttpDataResponse.
     * @param resolveError Error handler that accepts graph error codes and http status codes and returns a value to be retured
     * or undefined if unable to handle the code. Called with codes of diminishing specificity ending with the http status code.
     * @returns The first successfully returned value from a callback execution or undefined.
     */
    public static resolveGraphError<T1, T2>(error: XhrServiceError<T2 | GraphError> | InstrumentedHttpDataResponse<T2 | GraphError>, resolveError: (code: string | number) => T1): T1 {
        const errorResponse: InstrumentedHttpDataResponse<T2 | GraphError> = (error as XhrServiceError<T2 | GraphError>).httpResponse || error as InstrumentedHttpDataResponse<T2 | GraphError>;
        if (errorResponse && typeof (errorResponse.status) === 'number') {
            const status = errorResponse.status;
            const graphResponse = errorResponse.data as GraphError;
            if (graphResponse && graphResponse.error) {
                const innerErrors: Array<GraphInnerError> = [];

                let innerError = graphResponse.error;

                while (innerError) {
                    innerErrors.push(innerError);

                    innerError = innerError.innererror;
                }

                innerError = innerErrors.pop();
                while (innerError) {
                    const resolution = resolveError(innerError.code);

                    if (typeof (resolution) !== 'undefined') {
                        return resolution;
                    }
                    innerError = innerErrors.pop();
                }
            }
            return resolveError(status);
        }
        return undefined;
    }
}
